import { MESSAGE_EVENTS } from "@custom-hooks/useChat";
import { initializeSocket, disconnectSocket } from "@endpoints/socket";
import {
  fetchJobMessagesProfile,
  incrementRoomUnread,
  setJobMembersProfile,
} from "@store/actions/message.action";
import store from "@store/index";
import { playNotificationSound } from "@utils/helperFunction";
import { updateAndSortChatGroups } from "@utils/updateAndSortChatGroups";
import { useSnackbar } from "notistack";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const SocketContext = createContext<any>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const socketRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { logged_in, user, token } = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (logged_in && user) {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        console.error("❌ SocketProvider: No sessionId found");
        return;
      }

      // Initialize socket
      const socket = initializeSocket();
      socketRef.current = socket;

      // Add detailed event listeners
      socket?.on("connect", () => {
        console.log(
          "✅ SocketProvider: Socket connected successfully",
          socket.id
        );
      });

      socket?.on("disconnect", (reason) => {
        console.log("🔌 SocketProvider: Socket disconnected", reason);
      });

      socket?.on("connect_error", (err) => {
        console.error("❌ SocketProvider: Connection error", err.message);
      });

      // `newMessage` is the notification event — toast + sound only. Count and
      // ordering updates are handled optimistically by RECEIVE_MESSAGE below.
      socket?.on("newMessage", (message) => {
        const currentRoute = window.location.pathname;
        if (!currentRoute?.includes("messages")) {
          playNotificationSound();
          enqueueSnackbar(`Message : ${message?.message}`, {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
      });

      // Debug all incoming events.
      // NOTE: onAny listeners are NOT cleared by removeAllListeners() — they
      // must be removed with offAny(), or StrictMode/re-mounts stack duplicate
      // handlers and counts increment more than once per message.
      const handleAnyEvent = (event: string, ...args: any[]) => {
        if (event === "pong") return; // Filter out pong to reduce noise

        if (event === MESSAGE_EVENTS.RECEIVE_MESSAGE) {
          const message = args?.[0];
          // Reorder + refresh last message for the affected room.
          const updatedChatGroups = updateAndSortChatGroups(
            store.getState().messageReducer?.membersProfile,
            message
          );
          if (updatedChatGroups?.length) {
            store.dispatch(setJobMembersProfile(updatedChatGroups));
          } else {
            // Room not in the current list (e.g. brand-new conversation) — reconcile.
            store.dispatch(fetchJobMessagesProfile());
          }

          // Optimistically bump the unread badge unless the message is ours or
          // the room is already open on screen (useChat marks those seen).
          const selfIds = [user?._id, user?.userId].filter(Boolean);
          const isOwnMessage = selfIds.includes(message?.senderId);
          const openJobId = new URLSearchParams(window.location.search).get(
            "jobId"
          );
          const isViewingRoom =
            window.location.pathname.includes("messages") &&
            openJobId === message?.jobId;
          if (!isOwnMessage && !isViewingRoom) {
            store.dispatch(
              incrementRoomUnread({ jobId: message?.jobId, userId: user?._id })
            );
          }
        }
        console.log(`📨 SocketProvider: Received event [${event}]`, args?.[0]);
      };
      // Clear any catch-all left over from a previous mount before re-adding.
      socket?.offAny();
      socket?.onAny(handleAnyEvent);

      // Reconcile optimistic counts with the server when the tab regains focus.
      const handleWindowFocus = () => {
        store.dispatch(fetchJobMessagesProfile());
      };
      window.addEventListener("focus", handleWindowFocus);

      return () => {
        window.removeEventListener("focus", handleWindowFocus);
        if (socketRef.current) {
          socketRef.current.offAny();
          socketRef.current.removeAllListeners();
        }
      };
    } else {
      console.log(
        "🚫 SocketProvider: User not logged in, disconnecting socket"
      );
      disconnectSocket();
      socketRef.current = null;
    }
  }, [logged_in, user?._id]); // Re-run when user ID changes

  return (
    <SocketContext.Provider value={socketRef}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
